import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../supabase';

export interface SuspendedMember {
  user_id: string;
  display_name: string | null;
  username: string | null;
  discriminator: number | null;
  email: string | null;
  permission_role_system_key: string | null;
  permission_role_name: string | null;
  function_role_label: string | null;
  /** ISO timestamp the member was suspended. The RPC orders ascending so the
   *  consumer sees FIFO (oldest first) — the same order used by
   *  `reactivate_suspended_members` to refill seats on tier upgrade. */
  suspended_at: string;
}

export interface UseSuspendedMembers {
  members: ReadonlyArray<SuspendedMember>;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

interface SuspendedMemberRpcRow {
  user_id?: string | null;
  display_name?: string | null;
  username?: string | null;
  discriminator?: number | null;
  email?: string | null;
  permission_role_system_key?: string | null;
  permission_role_name?: string | null;
  function_role_label?: string | null;
  suspended_at?: string | null;
}

/**
 * ViewModel-level wrapper around the `public.list_suspended_members(p_org_id)`
 * RPC. Owner + Admin only — non-Owner/Admin callers receive an empty array
 * (or RPC-level forbidden); the consumer is the billing surface so the gate
 * is in `useCapability(orgId, 'billing.manage')` upstream.
 *
 * Rows are pre-ordered by `suspended_at ASC` (FIFO) so the downgrade-overage
 * picker and the reactivation queue render in the same order the server uses
 * to repopulate seats on tier upgrade.
 *
 * @param orgId — active org id; `null` keeps `members: []` + `loading: false`
 *   so consumers can call unconditionally.
 */
export function useSuspendedMembers(orgId: string | null): UseSuspendedMembers {
  const [members, setMembers] = useState<ReadonlyArray<SuspendedMember>>([]);
  const [loading, setLoading] = useState<boolean>(!!orgId);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (id: string) => {
    const { data, error: rpcErr } = await supabase.rpc(
      'list_suspended_members',
      { p_org_id: id }
    );
    if (rpcErr) {
      setError("Couldn't load suspended members. Please try again.");
      setLoading(false);
      return;
    }
    const raw = (data ?? []) as ReadonlyArray<SuspendedMemberRpcRow>;
    const out: SuspendedMember[] = [];
    for (const r of raw) {
      if (!r.user_id || !r.suspended_at) continue;
      out.push({
        user_id: r.user_id,
        display_name: r.display_name ?? null,
        username: r.username ?? null,
        discriminator:
          typeof r.discriminator === 'number' ? r.discriminator : null,
        email: r.email ?? null,
        permission_role_system_key: r.permission_role_system_key ?? null,
        permission_role_name: r.permission_role_name ?? null,
        function_role_label: r.function_role_label ?? null,
        suspended_at: r.suspended_at
      });
    }
    setMembers(out);
    setError(null);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!orgId) {
      setMembers([]);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    void load(orgId);
  }, [orgId, load]);

  const refetch = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    await load(orgId);
  }, [orgId, load]);

  return { members, loading, error, refetch };
}
